import React, { useMemo, useState, useEffect } from "react";
import {
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Badge,
  Row,
  Col,
  Alert,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "components/Common/TableContainer";
import { format } from 'date-fns';
import { FiDollarSign } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { 
  fetchRefundsRequest, 
  approveRefundRequest,
  markRefundRefundedRequest
} from "../../store/refund/actions";
import { db } from "helpers/firebase/firebaseConfig";
import { collection, addDoc, getDocs } from "firebase/firestore";

// Shared helper to format Firestore timestamps or date strings
const formatRequestedDate = (value, fallback) => {
  const dateVal =
    value?.seconds ? new Date(value.seconds * 1000) :
    value ? new Date(value) :
    fallback?.seconds ? new Date(fallback.seconds * 1000) :
    fallback ? new Date(fallback) :
    null;
  return dateVal ? format(dateVal, "dd/MM/yyyy") : "N/A";
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const resolveRefundBaseAmount = (source) => {
  return (
    toNumber(source?.gross_amount) ||
    toNumber(source?.original_service_amount) ||
    toNumber(source?.total_after_discount_before_fine) ||
    toNumber(source?.total_after_discount) ||
    toNumber(source?.gross_sale_amount) ||
    toNumber(source?.amount_paid) ||
    toNumber(source?.invoice_amount) ||
    toNumber(source?.amount) ||
    toNumber(source?.total_platform_revenue) ||
    (toNumber(source?.app_commission) + toNumber(source?.service_provider_commission))
  );
};

const deriveRefundAmounts = (source) => {
  const grossAmount = resolveRefundBaseAmount(source);

  const rawFineDeduction =
    toNumber(source?.fine_deduction_amount) ||
    toNumber(source?.fine_deductions) ||
    toNumber(source?.fine_deductions_total);

  const cancelledBy = String(source?.cancelled_by || "").toLowerCase();
  const fineChargedTo = String(
    source?.fine_charged_to || (cancelledBy === "app_user" || cancelledBy === "app__user" ? "app_user" : "provider")
  ).toLowerCase();
  const fineDeduction = fineChargedTo === "app_user" ? rawFineDeduction : 0;

  const netRefundAmount = Math.max(
    0,
    toNumber(source?.net_refund_amount ?? source?.invoice_amount ?? source?.amount) ||
      (grossAmount - fineDeduction)
  );

  return { grossAmount, fineDeduction, netRefundAmount };
};

// Merge invoice data with refund data to fill in missing fields
const enrichInvoiceWithRefundData = (invoice, refund) => {
  if (!invoice || !refund) return invoice;
  const amounts = deriveRefundAmounts(refund);
  
  return {
    ...invoice,
    // Use refund data as fallback for missing invoice fields
    customer_name: refund.customer_name || invoice.customer_name || "N/A",
    customer: invoice.customer || refund.customer_name || "N/A",
    service_name: invoice.service_name || refund.service_name || "N/A",
    service_provider_name: invoice.service_provider_name || refund.service_provider_name || "N/A",
    service_provider_id: invoice.service_provider_id || refund.service_provider_id,
    erp_item_code: invoice.erp_item_code || refund.commission_item_code,
    ksa_einv_qr: invoice.ksa_einv_qr || refund.ksa_einv_qr,
    // Ensure grand_total has a value
    grand_total: invoice.grand_total ?? amounts.grossAmount ?? 0,
    net_refund_amount: amounts.netRefundAmount,
    fine_deduction_amount: amounts.fineDeduction,
    gross_refund_amount: amounts.grossAmount,
  };
};

const Refund = () => {
  document.title = "طلبات الاسترجاع | DBSaloni Admin & Dashboard";

  const dispatch = useDispatch();
  const { refunds, loading, error, markingRefunded } = useSelector((state) => {
    return state.refund || {};
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [creditNotes, setCreditNotes] = useState([]);
  const [creditNotesLoading, setCreditNotesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);

  useEffect(() => {
    dispatch(fetchRefundsRequest());
    fetchCreditNotes();
  }, [dispatch]);

  const fetchCreditNotes = async () => {
    try {
      setCreditNotesLoading(true);
      const creditNotesSnapshot = await getDocs(collection(db, "credit_notes"));
      const notes = [];
      creditNotesSnapshot.forEach((doc) => {
        notes.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      // Sort by created_at (newest first)
      notes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setCreditNotes(notes);
    } catch (err) {
      console.error("Error fetching credit notes:", err);
    } finally {
      setCreditNotesLoading(false);
    }
  };

  useEffect(() => {

  }, [refunds, loading, error]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const viewDetails = (refund) => {
    setSelectedRefund(refund);
    toggleModal();
  };

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "index",
        Cell: ({ row }) => row.index + 1,
        width: 50
      },
      {
        Header: "رقم الفاتورة",
        accessor: "erp_invoice_id",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "اسم العميل",
        accessor: "customer_name",
        Cell: ({ value }) => value || "غير متوفر",
      },
      {
        Header: "المبلغ",
        accessor: "amount",
        Cell: ({ row }) => {
          const { netRefundAmount } = deriveRefundAmounts(row.original);
          const total = netRefundAmount;
          return `${total} ر.س`;
        },
      },
      {
        Header: "تاريخ الطلب",
        accessor: "requested_date",
        Cell: ({ value, row }) => formatRequestedDate(value, row.original?.requested_at),
      },
      {
        Header: "الحالة",
        accessor: "status",
        Cell: ({ row }) => {
          const { refunded, rejected } = row.original || {};
          const status = refunded
            ? { color: "success", text: "مسترجع" }
            : rejected
            ? { color: "danger", text: "مرفوضة" }
            : { color: "warning", text: "قيد الانتظار" };
          return <Badge color={status.color}>{status.text}</Badge>;
        },
      },
      {
        Header: "الإجراءات",
        accessor: "actions",
        Cell: ({ row }) => (
          <Button
            color="primary"
            size="sm"
            onClick={() => viewDetails(row.original)}
          >
            عرض التفاصيل
          </Button>
        ),
      },
    ],
    []
  );

  const creditNotesColumns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "index",
        Cell: ({ row }) => row.index + 1,
        width: 50
      },
      {
        Header: "رقم إشعار الاسترجاع",
        accessor: "credit_note_id",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "رقم الفاتورة الأصلية",
        accessor: "erp_invoice_id",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "اسم العميل",
        accessor: "customer_name",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: "المبلغ المسترجع",
        accessor: "amount",
        Cell: ({ value }) => `${value?.toFixed(2) || 0} ر.س`,
      },
      {
        Header: "تاريخ الإنشاء",
        accessor: "created_at",
        Cell: ({ value }) => {
          if (!value) return "N/A";
          return format(new Date(value), "dd/MM/yyyy HH:mm");
        },
      },
      {
        Header: "الحالة",
        accessor: "status",
        Cell: ({ value }) => {
          const statusMap = {
            completed: { color: "success", text: "مكتمل" },
            pending: { color: "warning", text: "قيد الانتظار" },
            failed: { color: "danger", text: "فشل" },
          };
          const status = statusMap[value] || { color: "secondary", text: value };
          return <Badge color={status.color}>{status.text}</Badge>;
        },
      },
      {
        Header: "الإجراءات",
        accessor: "actions",
        Cell: ({ row }) => (
          <Button
            color="info"
            size="sm"
            onClick={() => {
              setSelectedCreditNote(row.original);
              setShowCreditNoteModal(true);
            }}
          >
            عرض التفاصيل
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div className="page-content" dir="rtl">
      <Container fluid>
        <Breadcrumbs
          title="طلبات الاسترجاع"
          breadcrumbItem="إدارة استرجاعات المبالغ"
        />

        {/* Tabs */}
        <Card className="mb-3">
          <CardBody>
            <Nav tabs>
              <NavItem>
                <NavLink
                  href="#"
                  className={activeTab === "1" ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab("1");
                  }}
                >
                  <i className="bx bx-receipt me-2"></i>
                  طلبات الاسترجاع
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#"
                  className={activeTab === "2" ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab("2");
                  }}
                >
                  <i className="bx bx-check-circle me-2"></i>
                  إشعارات الاسترجاع ({creditNotes.length})
                </NavLink>
              </NavItem>
            </Nav>
          </CardBody>
        </Card>

        <TabContent activeTab={activeTab}>
          {/* Refunds Tab */}
          <TabPane tabId="1">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">جاري تحميل البيانات...</p>
              </div>
            ) : error ? (
              <Alert color="danger">
                <h5 className="alert-heading">حدث خطأ!</h5>
                <p>{error}</p>
              </Alert>
            ) : (
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={refunds || []}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    customPageSize={10}
                    className="custom-header-css"
                  />
                </CardBody>
              </Card>
            )}
          </TabPane>

          {/* Credit Notes Tab */}
          <TabPane tabId="2">
            {creditNotesLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">جاري تحميل بيانات إشعارات الاسترجاع...</p>
              </div>
            ) : creditNotes.length > 0 ? (
              <Card>
                <CardBody>
                  <TableContainer
                    columns={creditNotesColumns}
                    data={creditNotes}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    customPageSize={10}
                    className="custom-header-css"
                  />
                </CardBody>
              </Card>
            ) : (
              <Card>
                <CardBody className="text-center py-5">
                  <p className="text-muted">لا توجد إشعارات استرجاع حتى الآن</p>
                </CardBody>
              </Card>
            )}
          </TabPane>
        </TabContent>

        <RefundDetailsModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          refund={selectedRefund}
        />

        {/* Credit Note Details Modal */}
        <Modal isOpen={showCreditNoteModal} toggle={() => setShowCreditNoteModal(false)} size="lg" centered>
                  <ModalHeader toggle={() => setShowCreditNoteModal(false)} className="border-0">
                    <span className="modal-title">تفاصيل إشعار الاسترجاع</span>
                  </ModalHeader>
                  <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {selectedCreditNote && (
                      <>
                        {/* Header Info */}
                        <Card className="mb-3">
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5 className="card-title mb-0">معلومات الإشعار</h5>
                              <Badge color="success" pill className="px-3 py-2">
                                {selectedCreditNote.status === "completed" ? "مكتمل" : "قيد الانتظار"}
                              </Badge>
                            </div>
                    
                            <Row className="gy-3">
                              <Col md={6}>
                                <div className="d-flex flex-column gap-2">
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                                    <span className="text-muted">رقم إشعار الاسترجاع</span>
                                    <strong>{selectedCreditNote.credit_note_id || "N/A"}</strong>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                                    <span className="text-muted">رقم الفاتورة الأصلية</span>
                                    <strong>{selectedCreditNote.erp_invoice_id || "N/A"}</strong>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                                    <span className="text-muted">اسم العميل</span>
                
                                    <strong>{selectedCreditNote.customer_name || "N/A"}</strong>
                                  </div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="d-flex flex-column gap-2">
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                                    <span className="text-muted">المبلغ المسترجع</span>
                                    <strong className="text-danger">{selectedCreditNote.amount?.toFixed(2) || 0} ر.س</strong>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                                    <span className="text-muted">تاريخ الإنشاء</span>
                                    <strong>
                                      {selectedCreditNote.created_at 
                                        ? format(new Date(selectedCreditNote.created_at), "dd/MM/yyyy HH:mm") 
                                        : "N/A"}
                                    </strong>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                                    <span className="text-muted">بواسطة</span>
                                    <strong>{selectedCreditNote.created_by || "admin"}</strong>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>

                        {/* Invoice Details from credit_note_data */}
                        {selectedCreditNote.credit_note_data && (
                          <Card className="mb-3">
                            <CardBody>
                              <h5 className="card-title mb-3">تفاصيل الفاتورة</h5>
                      
                              <Row className="gy-2 mb-3">
                                <Col md={6}>
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                                    <span className="text-muted">تاريخ الفاتورة</span>
                                    <strong>{selectedCreditNote.credit_note_data.posting_date || "N/A"}</strong>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mt-2">
                                    <span className="text-muted">الإجمالي الأصلي</span>
                                    <strong>{Math.abs(selectedCreditNote.credit_note_data.base_grand_total || 0)} ر.س</strong>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                                    <span className="text-muted">حالة الفاتورة</span>
                                    <Badge color="info">{selectedCreditNote.credit_note_data.status || "N/A"}</Badge>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mt-2">
                                    <span className="text-muted">نوع الفاتورة</span>
                                    <Badge color={selectedCreditNote.credit_note_data.is_return ? "warning" : "secondary"}>
                                      {selectedCreditNote.credit_note_data.is_return ? "إشعار استرجاع" : "فاتورة عادية"}
                                    </Badge>
                                  </div>
                                </Col>
                              </Row>

                              {/* Items */}
                              {selectedCreditNote.credit_note_data.items && selectedCreditNote.credit_note_data.items.length > 0 && (
                                <div className="mt-3">
                                  <h6 className="mb-2">البنود</h6>
                                  <div className="table-responsive">
                                    <table className="table table-sm table-bordered">
                                      <thead className="table-light">
                                        <tr>
                                          <th>الخدمة</th>
                                          <th className="text-center">الكمية</th>
                                          <th className="text-end">السعر</th>
                                          <th className="text-end">الإجمالي</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {selectedCreditNote.credit_note_data.items.map((item, idx) => (
                                          <tr key={idx}>
                                            <td>{item.item_name || item.item_code}</td>
                                            <td className="text-center">{Math.abs(item.qty || 0)}</td>
                                            <td className="text-end">{item.rate || 0} ر.س</td>
                                            <td className="text-end fw-bold">{Math.abs(item.amount || 0)} ر.س</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Payment Details */}
                              {selectedCreditNote.credit_note_data.payments && selectedCreditNote.credit_note_data.payments.length > 0 && (
                                <div className="mt-3 p-3 bg-light rounded">
                                  <h6 className="mb-2">بيانات الدفع</h6>
                                  {selectedCreditNote.credit_note_data.payments.map((payment, idx) => (
                                    <div key={idx} className="d-flex justify-content-between mb-2">
                                      <span>{payment.mode_of_payment || "نقد"}</span>
                                      <strong>{Math.abs(payment.amount || 0)} ر.س</strong>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* QR Code */}
                              {selectedCreditNote.credit_note_data.ksa_einv_qr && (
                                <div className="mt-3 text-center p-3 border rounded">
                                  <p className="mb-2 fw-semibold text-muted">رمز QR للفاتورة</p>
                                  <img
                                    src={`https://node-server-eta-coral.vercel.app/erp-image?path=${encodeURIComponent(selectedCreditNote.credit_note_data.ksa_einv_qr)}`}
                                    alt="Invoice QR"
                                    style={{ width: 120, height: 120, objectFit: "contain" }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                </div>
                              )}
                            </CardBody>
                          </Card>
                        )}
                      </>
                    )}
                  </ModalBody>
                  <ModalFooter className="border-0">
                    <Button color="secondary" onClick={() => setShowCreditNoteModal(false)}>
                      إغلاق
                    </Button>
                  </ModalFooter>
                </Modal>
      </Container>
    </div>
  );
};

const RefundDetailsModal = ({ isOpen, toggle, refund }) => {
  const dispatch = useDispatch();
  const [invoice, setInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [refundError, setRefundError] = useState(null);
  const [refundResponse, setRefundResponse] = useState(null);
  const [erpConstants, setErpConstants] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState(null);

  const baseUrl = "https://node-server-eta-coral.vercel.app";


  const fetchInvoiceDetails = async (invoiceId) => {
    try {
      setLoadingInvoice(true);
      setInvoiceError(null);
      setQrImageUrl(null);

      const response = await fetch(
        `${baseUrl}/invoice-details/${encodeURIComponent(invoiceId)}`,
        {
          headers: { "Content-Type": "application/json",
            'x-api-key': process.env.REACT_APP_NODE_API_KEY
            
           }
        }
      );
      const json = await response.json();
    
      if (json.data && json.data.length > 0) {
        // Enrich invoice with refund data
        const enrichedInvoice = enrichInvoiceWithRefundData(json.data[0], refund);
        setInvoice(enrichedInvoice);
        
        if (enrichedInvoice.ksa_einv_qr) {
          setQrImageUrl(`${baseUrl}/erp-image?path=${encodeURIComponent(enrichedInvoice.ksa_einv_qr)}`);
        } else if (enrichedInvoice.qr_code) {
          setQrImageUrl(enrichedInvoice.qr_code);
        }
  
      } else {
        setInvoiceError("لم يتم العثور على الفاتورة");
      }
    } catch (err) {
      console.error("Error fetching invoice:", err);
      setInvoiceError(`خطأ: ${err.message}`);
    } finally {
      setLoadingInvoice(false);
    }
  };


  const fetchERPConstants = async () => {
    try {
      const snap = await getDocs(collection(db, "erp_constants"));
      if (!snap.empty) {
        // Assuming there's only one doc with all constants
        const doc = snap.docs[0];
        setErpConstants(doc.data());
      } else {
        // Fallback defaults
        setErpConstants({
          company: "DB Saloni",
          cost_center: "Main - DS",
          debit_to: "1310 - Debtors - DS",
          income_account: "4110 - Sales - DS",
          item_group: "Services",
          item_tax_template: "Standard Vat 15% - DS",
          item_type: "Service",
          payment_account: "1110 - Cash - DS",
        });
      }
    } catch (err) {
      console.error("Error fetching ERP constants:", err);
      // Use hardcoded fallback
      setErpConstants({
        company: "DB Saloni",
        cost_center: "Main - DS",
        debit_to: "1310 - Debtors - DS",
        income_account: "4110 - Sales - DS",
        item_group: "Services",
        item_tax_template: "Standard Vat 15% - DS",
        item_type: "Service",
        payment_account: "1110 - Cash - DS",
      });
    }
  };

  useEffect(() => {
    if (isOpen && refund?.erp_invoice_id) {
      fetchInvoiceDetails(refund.erp_invoice_id);
      fetchERPConstants();
      setRefundError(null);
      setRefundResponse(null);
      setQrImageUrl(null);
    }
  }, [isOpen, refund?.erp_invoice_id]);

  const parseServerMessages = (msgStr) => {
    try {
      const arr = JSON.parse(msgStr);
      return arr
        .map(m => {
          try { return JSON.parse(m)?.message || m; } catch { return m; }
        })
        .join(" | ");
    } catch {
      return msgStr;
    }
  };



  const approveAndCreateCreditNote = async () => {
    if (!invoice || !invoice.name) {
      setRefundError("بيانات الفاتورة غير مكتملة.");
      return;
    }
    if (!erpConstants) {
      setRefundError("لم يتم تحميل إعدادات ERP.");
      return;
    }
    setRefundError(null);
    setRefundResponse(null);
    setProcessing(true);

    const today = format(new Date(), "yyyy-MM-dd");
    const refundAmounts = deriveRefundAmounts(refund);
    const grandTotal = refundAmounts.netRefundAmount;

    const itemsPayload = [
      {
        item_code:
          invoice.erp_item_code ||
          refund?.commission_item_code ||
          invoice?.items?.[0]?.item_code ||
          "Service",
        qty: -1,
        rate: grandTotal,
        income_account: erpConstants.income_account,
        item_tax_template: erpConstants.item_tax_template,
      },
    ];

    const paymentsPayload =
      Array.isArray(invoice.payments) && invoice.payments.length > 0
        ? invoice.payments.map((p) => ({
            mode_of_payment: p.mode_of_payment || "Cash",
            account: p.account || erpConstants.payment_account,
            amount: -Math.abs(Number(p.amount ?? grandTotal)),
            base_amount: -Math.abs(Number(p.base_amount ?? grandTotal)),
          }))
        : [
            {
              mode_of_payment: "Cash",
              amount: -grandTotal,
              base_amount: -grandTotal,
              account: erpConstants.payment_account,
            },
          ];

    const payload = {
      customer: invoice.customer || invoice.customer_name,
      is_return: 1,
      docstatus: 1,
      company: erpConstants.company,
      return_against: invoice.name,
      update_outstanding_for_self: 0,
      is_pos: invoice.is_pos ? 1 : 0,
      cost_center: erpConstants.cost_center,
      posting_date: today,
      due_date: today,
      debit_to: erpConstants.debit_to,
      paid_amount: Math.abs(grandTotal),
      base_paid_amount: Math.abs(grandTotal),
      outstanding_amount: 0,
      grand_total: -Math.abs(grandTotal),
      base_grand_total: -Math.abs(grandTotal),
      items: itemsPayload,
      payments: paymentsPayload,
    };

    try {
      const res = await fetch("https://node-server-eta-coral.vercel.app/refund-credit-note", {
        method: "POST",
        headers: { "Content-Type": 
          "application/json", 
          "x-api-key": process.env.REACT_APP_NODE_API_KEY },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      // console.log("Credit Note Creation Response:", json);
      // console.log("Payload Sent:", payload);
      if (!res.ok || json?.error || json?.exc_type) {
        const serverMsg = json?._server_messages
          ? parseServerMessages(json._server_messages)
          : json?.message || json?.error || "فشل إنشاء إشعار الاسترجاع.";
        setRefundError(serverMsg);
      } else {
        const creditNoteData = json?.data || json;
  
        setRefundResponse(creditNoteData);
        
        // Save credit note to Firebase
        try {
          const creditNoteRef = await addDoc(collection(db, "credit_notes"), {
            refund_id: refund?.id,
            erp_invoice_id: invoice.name,
            credit_note_id: creditNoteData?.name,
            credit_note_data: creditNoteData,
            customer_name: creditNoteData?.customer_name || invoice.customer_name,
            amount: Math.abs(grandTotal),
            status: "completed",
            created_at: new Date().toISOString(),
            created_by: "admin",
          });
          // console.log("Credit note saved to Firebase:", creditNoteRef.id);
        } catch (fbError) {
          console.error("Error saving credit note to Firebase:", fbError);
        }
        
        if (refund?.id) {
          dispatch(approveRefundRequest(refund.id, creditNoteData));
        }
        
        // Close modal after 3 seconds
        setTimeout(() => {
          toggle();
        }, 3000);
      }
    } catch (e) {
      setRefundError(`خطأ في الاتصال: ${e.message}`);
    } finally {
      setProcessing(false);
    }
  };


  if (!refund) return null;
  const refundAmounts = deriveRefundAmounts(refund);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="border-0">
        <span className="modal-title">تفاصيل طلب الاسترجاع</span>
      </ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loadingInvoice ? (
          <div className="text-center">
            <Spinner color="primary" />
            <p className="mt-2">جاري تحميل بيانات الفاتورة...</p>
          </div>
        ) : invoiceError ? (
          <Alert color="danger">{invoiceError}</Alert>
        ) : invoice ? (
          <>
            <Card className="mb-3">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">معلومات الفاتورة</h5>
                  <Badge color="info" pill className="px-3 py-2">
                    رقم الفاتورة: {invoice.name || refund?.erp_invoice_id || "N/A"}
                  </Badge>
                </div>

                <Row className="gy-3">
                  <Col md={7}>
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                        <span className="text-muted">اسم العميل</span>
                        <strong>{refund.customer_name || "N/A"}</strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                        <span className="text-muted">التاريخ</span>
                        <strong>{invoice.posting_date || "N/A"}</strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center border-bottom pb-2">
                        <span className="text-muted">المبلغ الإجمالي</span>
                        <strong>{invoice.gross_refund_amount || 0} ر.س</strong>

                      </div>
                     
                    </div>
                  </Col>
                  <Col md={5} className="d-flex justify-content-center align-items-center">
                    {qrImageUrl ? (
                      <div className="text-center p-3 border rounded">
                        <p className="mb-2 fw-semibold">رمز QR للفاتورة</p>
                        <img
                          src={qrImageUrl}
                          alt="Invoice QR"
                          style={{ width: 140, height: 140, objectFit: "contain" }}
                        />
                      </div>
                    ) : (
                      <div className="text-center text-muted">
                        <p className="mb-1">لا يوجد رمز QR</p>
                        <small>سيظهر هنا عند توفره</small>
                      </div>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">معلومات طلب الاسترجاع</h5>
                  <Badge color={
                    refund.refunded
                      ? "success"
                      : refund.rejected
                      ? "danger"
                      : "warning"
                  } className="px-3 py-2">
                    {refund.refunded ? "مسترجع" : refund.rejected ? "مرفوضة" : "قيد الانتظار"}
                  </Badge>
                </div>

                <Row className="gy-2">
                  <Col md={4}>
                    <div className="d-flex flex-column border rounded p-3 h-100">
                      <span className="text-muted">تاريخ الطلب</span>
                      <strong>{formatRequestedDate(refund.requested_date, refund.requested_at)}</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex flex-column border rounded p-3 h-100">
                      <span className="text-muted">المبلغ المسترجع</span>
                      <strong>{refundAmounts.netRefundAmount} ر.س</strong>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="d-flex flex-column border rounded p-3 h-100">
                      <span className="text-muted">رقم فاتورة ERP</span>
                      <strong>{refund.erp_invoice_id || "N/A"}</strong>
                    </div>
                  </Col>

                </Row>

                <Alert color="light" className="mt-3 mb-0 border">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><strong>المبلغ قبل الخصم</strong></span>
                    <span>{refundAmounts.grossAmount.toFixed(2)} ر.س</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-1 text-danger">
                    <span><strong>الغرامة المخصومة</strong></span>
                    <span>- {refundAmounts.fineDeduction.toFixed(2)} ر.س</span>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between align-items-center fw-bold text-success">
                    <span>صافي مبلغ الاسترجاع (ZATCA/ERP)</span>
                    <span>{refundAmounts.netRefundAmount.toFixed(2)} ر.س</span>
                  </div>
                </Alert>

                {refund.reason && (
                  <Alert color="info" className="mt-3 mb-0">
                    <strong>سبب الاسترجاع:</strong> {refund.reason}
                  </Alert>
                )}
              </CardBody>
            </Card>

            {refundError && (
              <Alert color="danger">{refundError}</Alert>
            )}
            
            {refundResponse?.name && (
              <Card className="mb-3 border-success">
                <CardBody className="bg-light-success">
                  <div className="d-flex align-items-center mb-2">
                    <i className="bx bx-check-circle text-success me-2" style={{ fontSize: "24px" }}></i>
                    <h5 className="mb-0 text-success">تم إنشاء إشعار الاسترجاع بنجاح</h5>
                  </div>
                  <Alert color="success" className="mb-0">
                    تم إنشاء إشعار دائن بنجاح ✓
                  </Alert>
                  <Row className="mt-3">
                    <Col md={6}>
                      <p className="mb-1"><strong>رقم إشعار الاسترجاع:</strong></p>
                      <p className="text-primary fw-bold">{refundResponse.name}</p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-1"><strong>تاريخ الإنشاء:</strong></p>
                      <p>{refundResponse.creation ? format(new Date(refundResponse.creation), "dd/MM/yyyy HH:mm") : "N/A"}</p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <p className="mb-1"><strong>المبلغ المسترجع:</strong></p>
                      <p className="text-success fw-bold">{Math.abs(refundResponse.grand_total || 0)} ر.س</p>
                    </Col>
                    <Col md={6}>
                      <p className="mb-1"><strong>الحالة:</strong></p>
                      <Badge color="success">{refundResponse.status || "مكتمل"}</Badge>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}
          </>
        ) : null}
      </ModalBody>
      <ModalFooter className="border-0">
        <Button color="secondary" onClick={toggle}>
          إغلاق
        </Button>
        {invoice && !refundResponse?.name && !refund.refunded && (
          <>
            <Button
              color="success"
              onClick={approveAndCreateCreditNote}
              disabled={processing || !invoice || refund.refunded}
            >

              {processing ? <Spinner size="sm" className="me-1" /> : null}
              الموافقة وإنشاء إشعار الاسترجاع
            </Button>

          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default Refund;
